/**
 * Embroidery enrichment for the cart drawer.
 *
 * Embroidery line items carry raw option properties that must be turned into readable rows,
 * plus a Design ID that only the enrichment endpoint can confirm. This mirrors the live
 * behaviour of assets/cart-embroidery-update.js, which is the implementation actually running
 * on the cart page.
 *
 * Note: sections/main-cart-items.liquid ALSO contains an embroidery block, but it is dead —
 * there is a `return false;` immediately after its updateEmbroideryProductsInCart() call, so
 * the ~80 lines below never execute. That dead copy renders "Add Brighton Heart?" and "Heart
 * Color" rows; the live file has both commented out. This follows the LIVE behaviour.
 *
 * Results are cached in sessionStorage keyed on the Shopify line key, which already includes
 * a hash of the line's properties — so a change to any option produces a new key and the
 * cache can never serve a stale design.
 *
 * No module system in assets/ — attaches window.BrightonDrawerEmbroidery.
 */
(function () {
  if (window.BrightonDrawerEmbroidery) return;

  const ENDPOINT = 'https://app.brighton.com/embroidery/?09';
  const CACHE_PREFIX = 'brighton:emb:v1:';

  /**
   * Symbol option keys whose display label is identical to the property name.
   *
   * DUPLICATED from assets/cart-embroidery-update.js. A Google Sheet is the source of truth
   * for these labels, and new ones must be added to BOTH lists until the legacy cart page is
   * retired. Kept separate deliberately: sharing one array would mean editing a file the cart
   * page still depends on.
   */
  const SYMBOL_PROPERTIES = [
    'Left Symbol', 'Center Symbol', 'Right Symbol',
    '3/4in Left Symbol', '3/4in Center Symbol', '3/4in Right Symbol',
    '1in Left Symbol', '1in Center Symbol', '1in Right Symbol',
    '1.25in Left Symbol', '1.25in Center Symbol', '1.25in Right Symbol',
    '1.5in Left Symbol', '1.5in Center Symbol', '1.5in Right Symbol',
    '2in Left Symbol', '2in Center Symbol', '2in Right Symbol',
    'Symbol Text Placement',
    'Above Text Small Symbol', 'Above Text Small Symbol_1', 'Above Text Small Symbol_2', 'Above Text Small Symbol_3',
    'Below Text Small Symbol', 'Below Text Small Symbol_1', 'Below Text Small Symbol_2', 'Below Text Small Symbol_3',
    'Above Text Medium Symbol', 'Above Text Medium Symbol_1', 'Above Text Medium Symbol_2', 'Above Text Medium Symbol_3',
    'Below Text Medium Symbol', 'Below Text Medium Symbol_1', 'Below Text Medium Symbol_2', 'Below Text Medium Symbol_3',
    'Above Text Large Symbol', 'Above Text Large Symbol_1', 'Above Text Large Symbol_2', 'Above Text Large Symbol_3',
    'Below Text Large Symbol', 'Below Text Large Symbol_1', 'Below Text Large Symbol_2', 'Below Text Large Symbol_3',
  ];

  /** Option values arrive with CRLF-joined variants; only the first line is the label. */
  const firstLine = (value) => String(value == null ? '' : value).split('\r\n')[0];

  const splitLines = (value) => String(value == null ? '' : value).split('\r\n');

  /**
   * Builds the display rows for an embroidery line, in the same order and with the same
   * labels as cart-embroidery-update.js.
   */
  const buildRows = (properties) => {
    const props = properties || {};
    const rows = [];
    const push = (label, value) => {
      if (value === undefined || value === null || value === '') return;
      rows.push({ label, value: String(value) });
    };

    if (props['Select A Thread Color'] !== undefined) {
      push('Thread Color', props['Select A Thread Color']);
    }

    // Text variants are mutually exclusive; the final branch is the monogram fallback.
    if (props['Your Text'] !== undefined) {
      push('Font Size', firstLine(props['Select Text Size']));
      const lines = splitLines(props['Your Text']);
      if (lines.length > 1) {
        push('Text (line 1)', lines[0]);
        push('Text (line 2)', lines[1]);
      } else {
        push('Text', lines[0]);
      }
    } else if (props['Insert Your Text (up to two lines)'] !== undefined) {
      push('Font Size', firstLine(props['Select Text Size']));
      const lines = splitLines(props['Insert Your Text (up to two lines)']);
      push('Text (line 1)', lines[0]);
      if (lines.length > 1) push('Text (line 2)', lines[1]);
    } else if (props['Insert Your Text (up to one line)'] !== undefined) {
      push('Font Size', firstLine(props['Select Text Size']));
      const lines = splitLines(props['Insert Your Text (up to one line)']);
      push('Text (line 1)', lines[0]);
      if (lines.length > 1) push('Text (line 2)', lines[1]);
    } else {
      push('Monogram Style', props['Select Monogram Style']);
      push('Text', props['Insert Your Initials']);
    }

    SYMBOL_PROPERTIES.forEach((key) => {
      if (props[key] !== undefined) push(key, props[key]);
    });

    return rows;
  };

  const cacheGet = (lineKey) => {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + lineKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };

  const cacheSet = (lineKey, value) => {
    try {
      sessionStorage.setItem(CACHE_PREFIX + lineKey, JSON.stringify(value));
    } catch (e) {
      // Private mode or a full quota — enrichment just won't be cached.
    }
  };

  /** In-flight requests, so several drawer opens don't re-post the same line. */
  const inFlight = new Map();

  const fetchDesignId = (item) => {
    const emb = item.embroidery;
    const body = new URLSearchParams();
    Object.entries(emb.rawProperties || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null) body.append(k, v);
    });
    body.append('sku', item.sku || '');
    body.append('title', emb.lineTitle || item.title || '');
    body.append('color', emb.colour || item.variantTitle || '');

    return fetch(ENDPOINT, { method: 'POST', body })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => (json && json.confirmedDesignID) || null);
  };

  /**
   * Returns { designId, previewUrl, rows } for an embroidery line. Rows are derived locally
   * and available immediately; only the confirmed Design ID needs the network, so a failed
   * request degrades to showing everything except the ID.
   */
  const enrich = (item) => {
    if (!item || !item.embroidery) return Promise.resolve(null);

    const rows = buildRows(item.embroidery.rawProperties);
    const previewUrl = item.embroidery.previewUrl || null;

    const cached = cacheGet(item.key);
    if (cached) {
      return Promise.resolve({ designId: cached.designId, previewUrl, rows });
    }

    if (inFlight.has(item.key)) return inFlight.get(item.key);

    const promise = fetchDesignId(item)
      .catch(() => null)
      .then((designId) => {
        if (designId) cacheSet(item.key, { designId });
        inFlight.delete(item.key);
        return { designId, previewUrl, rows };
      });

    inFlight.set(item.key, promise);
    return promise;
  };

  window.BrightonDrawerEmbroidery = { SYMBOL_PROPERTIES, buildRows, enrich };
})();
